<script>
  import { identity, last } from 'ramda';
  import { onMount, tick } from 'svelte';
  import { fade } from 'svelte/transition';
  
  import Sidebar, { SidebarLink, SidebarHeading } from '@commercebear/components/Sidebar';
  import Icon from '@commercebear/components/Icon';
  import Button, { SMALL_ICON } from '@commercebear/components/Button';
  import Link, { TEXT } from '@commercebear/components/Link';
  import Spotlight from '@commercebear/components/Spotlight';
  
  import { useJobsDialog } from '$lib/components/JobsDialogProvider';
  import SidebarDropdown from '$lib/components/SidebarDropdown';
  import Logo from '$lib/components/Logo';
  
  import { useCurrentOrganization } from '$providers/CurrentOrganizationProvider';
  
  import getUrlHash from '$util/getUrlHash';
  import { getStores } from '$app/stores';

  const { page } = getStores();

  const currentOrganization = useCurrentOrganization();

  export let isMobile;
  export let pinned;
  $: pinned = !isMobile;
  let open;
  let autoOpened;

  let spotLight;
  let navSidebar;

  function show() {
    open = true;
    spotLight?.show();
  }

  function hide() {
    open = false;
    spotLight?.hide();
    setTimeout(() => navSidebar?.addEventListener('mouseenter', show, { once: true }));
  }

  export function toggle() {
    if (open) {
      hide();
    } else {
      show();
    }
  }

  $: {
    open = pinned || autoOpened;
    if (pinned) {
      spotLight?.hide();
    } else if (!autoOpened && !pinned) {
      hide();
    }
  }
  function togglePinned() {
    pinned = !pinned;
  }
  function spotlightHide() {
    if (isMobile) {
      open = false;
    }
  }

  const uuid = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;
  /** @param {String} pagePath */
  function activeSidebarLink(pagePath) {
    const segments = pagePath.split('/').filter(identity);
    if (last(segments)?.match(uuid)) {
      segments.pop();
    }

    if (pagePath.endsWith('products/status')) {
      return 'catalog-status';
    }
    if (pagePath.endsWith('products/catalog')) {
      return 'catalog';
    }
    if (pagePath.endsWith('publish')) {
      return 'publish';
    }
    if (pagePath.endsWith('tasks')) {
      return 'tasks';
    }

    return undefined;
  }

  $: currentRoute = activeSidebarLink($page.url.pathname);

  function organizationUrl(path) {
    return `/organizations/${$currentOrganization.id}${path}`;
  }

  // Need to check if jobs should be enabled before we overwrite the url with any filters
  const jobsOnLoad = getUrlHash() === 'jobs';
  const jobsDialog = useJobsDialog();

  onMount(async () => {
    await tick();
    if (jobsOnLoad) {
      $jobsDialog?.show();
    }
  });

  function showSidebarMenu() {
    autoOpened = true;
  }
  function dismissSidebarMenu() {
    autoOpened = false;
  }
</script>

<Spotlight
  bind:this={spotLight}
  let:spotlight
  auto={false}
  on:hide={spotlightHide}>
  <div bind:this={navSidebar}
    class='nav-sidebar'
    class:open
    data-cy='nav-sidebar'
    use:spotlight
    on:mouseenter|once={() => !open && show()}
    on:mouseleave={() => !pinned && !autoOpened && hide()}>
    <header class:open>
      <a href={organizationUrl('/products/status')}>
        <Logo/>
      </a>
      {#if open}
        <Button secondary size={SMALL_ICON} on:click={togglePinned}>
          {#if pinned}
            <img src='/images/unpin-icon.svg' alt='unpin icon'/>
          {:else}
            <Icon icon='push_pin' />
          {/if}
        </Button>
      {/if}
    </header>

    <Sidebar bind:open {currentRoute} testid='sidebar-main'>
      <SidebarHeading>Manage</SidebarHeading>
      <SidebarLink icon='store' name='catalog' href={organizationUrl('/products/catalog')} testid='sidebar-catalog'>Catalog</SidebarLink>
      <SidebarLink icon='list_alt' name='catalog-status' href={organizationUrl('/products/status')} testid='sidebar-catalog-status'>Channel Status</SidebarLink>
      <SidebarLink icon='history' name='tasks' href={organizationUrl('/tasks')} testid='sidebar-tasks'>Task History</SidebarLink>
      <SidebarLink icon='publish' name='publish' href='/publish' testid='sidebar-assets-images'>Publish SKUs</SidebarLink>
    </Sidebar>

    {#if open}
      <footer in:fade={{ delay: 100 }}>
        <SidebarDropdown on:show={showSidebarMenu} on:dismiss={dismissSidebarMenu}/>
        <div class='footer-links'>
          <Link appearance={TEXT} external href='https://commercebear.zendesk.com/hc/en-us'>
            <div class='help-link'>
              Help Center
              <Icon size='16' icon='help' />
            </div>
          </Link>
          <Link appearance={TEXT} external href='https://commercebear.com'>CommerceBear &copy; 2024</Link>
        </div>
      </footer>
    {/if}
  </div>
</Spotlight>

<style lang='postcss'>
  header {
    --Logo--size: 36px;

    background: var(--color--white);
    padding: var(--spacing--m) var(--spacing--s);
    display: flex;
    align-items: center;

    &.open {
      padding: var(--spacing--m);
      justify-content: space-between;
    }
  }

  footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0 var(--spacing--m);
    box-sizing: border-box;
    margin-top: auto;

    & .footer-links {
      --Link--color: var(--color--info);

      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing--xs);
      margin: var(--spacing--m);
      font: var(--font--outfit-12);
      font-weight: 500;

      & .help-link {
        display: flex;
        align-items: center;
        gap: var(--spacing--xxs);
      }
    }
  }

  .nav-sidebar {
    display: flex;
    flex-direction: column;
    background: var(--color--white);
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    transition: width var(--duration--fast) var(--ease--on-screen);
    width: 60px;

    &.open {
      width: 240px;
    }
  }

  @media (--screen-width--below-tablet) {
    .nav-sidebar:not(.open) {
      width: 0;
    }
  }
</style>
